import { useId } from "react"
import type { LucideIcon, LucideProps } from "lucide-react"

export type PrimaryGradientStops = { start: string; end: string; angle: number }

type PrimaryGradientIconProps = Omit<LucideProps, "color" | "children"> & {
  icon: LucideIcon
  color: string
  gradient?: PrimaryGradientStops
}

function percent(value: number) {
  return `${Number(value.toFixed(3))}%`
}

export function PrimaryGradientDefinition({ id, gradient }: { id: string; gradient: PrimaryGradientStops }) {
  const radians = gradient.angle * Math.PI / 180
  const dx = 50 * Math.sin(radians)
  const dy = -50 * Math.cos(radians)
  return (
    <defs>
      <linearGradient id={id} x1={percent(50 - dx)} y1={percent(50 - dy)} x2={percent(50 + dx)} y2={percent(50 + dy)}>
        <stop offset="0%" stopColor={gradient.start} />
        <stop offset="100%" stopColor={gradient.end} />
      </linearGradient>
    </defs>
  )
}

export function PrimaryGradientIcon({ icon: Icon, color, gradient, ...props }: PrimaryGradientIconProps) {
  const id = `primary-icon-${useId().replace(/[^a-zA-Z0-9_-]/g, "-")}`
  const ariaHidden = props["aria-hidden"] ?? (!props["aria-label"] && !props["aria-labelledby"])
  if (!gradient) return <Icon {...props} aria-hidden={ariaHidden} color={color} />

  return (
    <Icon {...props} aria-hidden={ariaHidden} color={`url(#${id}) ${color}`}>
      <PrimaryGradientDefinition id={id} gradient={gradient} />
    </Icon>
  )
}
